/**
 * What kind of thing an asset IS, and everything that follows from it.
 *
 * The pillar author's only media decision is which member of
 * `CresultcardMediaKind` applies. Aspect, fit, whether the frame bleeds to the
 * plate edge, whether the intrinsic pixels are allowed to shape the box and
 * what the designed absence says are all read from this table — which is why
 * the kit has no `height`, `width`, `objectFit`, `aspect` or `radius` prop on
 * a media frame. Eleven hard-coded hero heights and nine predicted container
 * widths collapse into three scales times twelve kinds resolved here, plus one
 * `ResizeObserver` per frame.
 */
import type { HTMLAttributeReferrerPolicy, ImgHTMLAttributes } from "react";
import {
  CIMAGE_OBJECT_FIT,
  type CimageObjectFit,
} from "@/components/ui/Cimage";
import { CresultcardMediaScale } from "./cresultcard.types";

/**
 * The request policy every vendor asset in the kit is fetched under. A referrer
 * is the one header some CDNs use to decide whether to answer at all, and
 * sending our own is both a privacy leak and a way to get a 403.
 */
export const CRC_MEDIA_REFERRER_POLICY: HTMLAttributeReferrerPolicy =
  "no-referrer";
export const CRC_MEDIA_LOADING: ImgHTMLAttributes<HTMLImageElement>["loading"] =
  "lazy";
export const CRC_MEDIA_DECODING: ImgHTMLAttributes<HTMLImageElement>["decoding"] =
  "async";

export enum CresultcardMediaKind {
  /** Property, venue, place, product, event, article lead. */
  Photograph = "photograph",
  /**
   * A photograph the capture is too small to crop.
   *
   * Same subject and same aspect ladder as `Photograph`, and the same designed
   * absence — the difference is the two halves of `Photograph`'s policy that a
   * small capture cannot honour. `cover` on a 137×92 detail thumbnail crops an
   * already-tiny frame, and `capsToIntrinsic: false` blows it up to fill a
   * 516px hero at ≈4× (§8.14, D11). This kind contains the asset on its plate
   * and never grows it past 1:1, so the frame is still the composition's shape
   * and the picture is still only as large as the pixels behind it.
   */
  Still = "still",
  /** Book jacket, album art, poster, sheet music. */
  Cover = "cover",
  /**
   * A retail cut-out photographed on a transparent or white ground.
   *
   * Neither a `Photograph` — for a product the object IS the content and the
   * background is nothing, so `cover` crops the subject itself (PW-004) — nor a
   * `Mark`, whose optical padding is a logo's and whose absence says a logo
   * failed. It is contained, capped at its own pixels, and carries a smaller
   * measure of air than a logo does so the object is not edge to edge.
   */
  Product = "product",
  /**
   * The same artwork as `Cover`, presented as a disc — the shape a ring track
   * is drawn around. It is a separate kind rather than a flag because the
   * circular crop is a presentation decision the kit owns, and because a
   * sleeve's designed absence still says `Cover unavailable` where a
   * `Portrait`'s would not.
   */
  Disc = "disc",
  /** Company logo, carrier, favicon, publisher, exchange. */
  Mark = "mark",
  /** Person, artist, author, host. */
  Portrait = "portrait",
  /** A photograph whose SHAPE is the information. */
  Native = "native",
  /** A place, a route, a region. */
  MapTile = "mapTile",
  /** Video poster, thumbnail capture. */
  Frame = "frame",
  /** Floor plan, seat map, filing page, chart capture. */
  Diagram = "diagram",
  /** A series we draw ourselves; the chart primitive owns its own box. */
  Chart = "chart",
}

/**
 * `null` means the frame has no declared aspect and takes its shape from the
 * asset's own pixels. It is not a default and it is not "auto": a kind that
 * carries it REQUIRES intrinsic dimensions, and says so in `needsIntrinsic`.
 */
export type CresultcardAspect = number | null;

export interface CresultcardMediaPolicy {
  /** Width ÷ height of the container, per scale. */
  readonly aspect: Record<CresultcardMediaScale, CresultcardAspect>;
  readonly fit: CimageObjectFit;
  /**
   * At `Hero`, does the asset run to the plate edge? Letterboxing a bled cover
   * paints the plate's gradient behind the artwork and reads as a rendering
   * fault, so only kinds whose crop is deliberate bleed.
   */
  readonly bleedsAtHero: boolean;
  /** The frame adopts the asset's own ratio when the pixels are known. */
  readonly adoptsIntrinsic: boolean;
  /**
   * Whether the frame may learn the asset's shape from the DECODED bitmap when
   * the caller declared no intrinsics.
   *
   * True only where the declared aspect is a stand-in the frame is MEANT to
   * leave behind: a kind whose shape is the information has no honest fixed
   * ratio, and a proxy thumbnail that arrives 240px wide is 240px wide whether
   * or not anybody wrote that down. False wherever a declared ratio is the
   * contract — a cover's frame is reserved 2:3 before paint and re-laying it
   * out once the pixels land is exactly the reflow §4.2 forbids, which is why
   * two families withhold their intrinsics from `Cover` on purpose.
   */
  readonly adoptsDecodedShape: boolean;
  /** The frame cannot be drawn honestly without the asset's pixel dimensions. */
  readonly needsIntrinsic: boolean;
  /**
   * Whether the INLINE asset is capped at its own pixels.
   *
   * It is true only where the intrinsic size is part of the meaning: a jacket,
   * a logo or a captured document blown past its capture is a claim the pixels
   * do not support, so it sits at 1:1 on the plate. It is false wherever the
   * crop is the point — a photograph, a frame grab, a portrait — because there
   * the cap is what stops `cover` from ever covering, and a 304×152 lead
   * stranded inside a 516px hero with bare plate around it is not restraint, it
   * is an unfilled frame. The lightbox caps unconditionally either way (§4.4).
   */
  readonly capsToIntrinsic: boolean;
  /** A circular frame publishes no radius rung of its own. */
  readonly circular: boolean;
  /** Optical padding as a fraction of the frame, for marks that need air. */
  readonly opticalPadding: number;
}

const ASPECT_SQUARE = 1;
const ASPECT_THREE_TWO = 3 / 2;
const ASPECT_SIXTEEN_NINE = 16 / 9;
const ASPECT_FOUR_THREE = 4 / 3;
const ASPECT_TWO_THREE = 2 / 3;

/** A logo needs visible air on all four sides or it reads as a crop. */
const OPTICAL_PADDING_MARK = 0.12;
/**
 * A cut-out needs less: the object is the subject rather than a glyph, and a
 * logo's measure of air on a product shot reads as an undersized picture.
 */
const OPTICAL_PADDING_PRODUCT = 0.05;

/**
 * A `Native` frame's derived height is bounded, because one 800×1200 asset in
 * a 236px column wants 354px and would own the whole card. When the ceiling
 * bites, the frame says so and the true pixels go in the figure row: a ceiling
 * that announces itself is honest, a silent crop is not.
 *
 * The ceiling is per SCALE, because one number cannot be right for all three: a
 * row's lead slot is a 40–52px square (§4.2, D12b) and a single 300px constant
 * let a thumbnail grow to six times its own slot and break the row grid's
 * height contract. `Hero` keeps 300 — the mosaic's stated ceiling, where 236px
 * of width earns that much block — and `Face` takes the rail card's stated 198
 * (§8.8), which is already most of a rail card's height.
 */
export const CRC_NATIVE_HEIGHT_CEILING: Record<CresultcardMediaScale, number> =
  {
    [CresultcardMediaScale.Thumb]: 52,
    [CresultcardMediaScale.Face]: 198,
    [CresultcardMediaScale.Hero]: 300,
  };

/**
 * How long a frame waits for a host that accepted the connection and then said
 * nothing.
 *
 * A hung request fires neither `load` nor `error`, so without this the frame
 * stays in `Loading` for the life of the page and the designed absence — the
 * state a quarter of two pillars' rows live in (§4.3) — never renders at all.
 * Ten seconds is past any asset a reader would still be waiting for and well
 * short of a browser's own connection timeout, so it is our answer rather than
 * the network's.
 */
export const CRC_MEDIA_LOAD_TIMEOUT_MS = 10_000;

/** Outside this band a cover's own ratio is a bad capture, not a tall jacket. */
export const CRC_COVER_INTRINSIC_MIN = 0.5;
export const CRC_COVER_INTRINSIC_MAX = 1;

export const CRC_MEDIA_POLICY: Record<
  CresultcardMediaKind,
  CresultcardMediaPolicy
> = {
  [CresultcardMediaKind.Photograph]: {
    aspect: {
      [CresultcardMediaScale.Thumb]: ASPECT_SQUARE,
      [CresultcardMediaScale.Face]: ASPECT_THREE_TWO,
      [CresultcardMediaScale.Hero]: ASPECT_SIXTEEN_NINE,
    },
    fit: CIMAGE_OBJECT_FIT.Cover,
    bleedsAtHero: true,
    adoptsIntrinsic: false,
    adoptsDecodedShape: false,
    needsIntrinsic: false,
    capsToIntrinsic: false,
    circular: false,
    opticalPadding: 0,
  },
  [CresultcardMediaKind.Still]: {
    aspect: {
      [CresultcardMediaScale.Thumb]: ASPECT_SQUARE,
      [CresultcardMediaScale.Face]: ASPECT_THREE_TWO,
      [CresultcardMediaScale.Hero]: ASPECT_SIXTEEN_NINE,
    },
    fit: CIMAGE_OBJECT_FIT.Contain,
    bleedsAtHero: false,
    adoptsIntrinsic: false,
    adoptsDecodedShape: false,
    needsIntrinsic: false,
    capsToIntrinsic: true,
    circular: false,
    opticalPadding: 0,
  },
  [CresultcardMediaKind.Cover]: {
    aspect: {
      [CresultcardMediaScale.Thumb]: ASPECT_SQUARE,
      [CresultcardMediaScale.Face]: ASPECT_TWO_THREE,
      [CresultcardMediaScale.Hero]: ASPECT_TWO_THREE,
    },
    fit: CIMAGE_OBJECT_FIT.Contain,
    bleedsAtHero: false,
    adoptsIntrinsic: true,
    adoptsDecodedShape: false,
    needsIntrinsic: false,
    capsToIntrinsic: true,
    circular: false,
    opticalPadding: 0,
  },
  [CresultcardMediaKind.Product]: {
    aspect: {
      [CresultcardMediaScale.Thumb]: ASPECT_SQUARE,
      [CresultcardMediaScale.Face]: ASPECT_FOUR_THREE,
      [CresultcardMediaScale.Hero]: ASPECT_FOUR_THREE,
    },
    fit: CIMAGE_OBJECT_FIT.Contain,
    bleedsAtHero: false,
    adoptsIntrinsic: false,
    adoptsDecodedShape: false,
    needsIntrinsic: false,
    capsToIntrinsic: true,
    circular: false,
    opticalPadding: OPTICAL_PADDING_PRODUCT,
  },
  [CresultcardMediaKind.Disc]: {
    aspect: {
      [CresultcardMediaScale.Thumb]: ASPECT_SQUARE,
      [CresultcardMediaScale.Face]: ASPECT_SQUARE,
      [CresultcardMediaScale.Hero]: ASPECT_SQUARE,
    },
    fit: CIMAGE_OBJECT_FIT.Cover,
    bleedsAtHero: false,
    adoptsIntrinsic: false,
    adoptsDecodedShape: false,
    needsIntrinsic: false,
    capsToIntrinsic: false,
    circular: true,
    opticalPadding: 0,
  },
  [CresultcardMediaKind.Mark]: {
    aspect: {
      [CresultcardMediaScale.Thumb]: ASPECT_SQUARE,
      [CresultcardMediaScale.Face]: ASPECT_SQUARE,
      [CresultcardMediaScale.Hero]: ASPECT_SQUARE,
    },
    fit: CIMAGE_OBJECT_FIT.Contain,
    bleedsAtHero: false,
    adoptsIntrinsic: false,
    adoptsDecodedShape: false,
    needsIntrinsic: false,
    capsToIntrinsic: true,
    circular: false,
    opticalPadding: OPTICAL_PADDING_MARK,
  },
  [CresultcardMediaKind.Portrait]: {
    aspect: {
      [CresultcardMediaScale.Thumb]: ASPECT_SQUARE,
      [CresultcardMediaScale.Face]: ASPECT_SQUARE,
      [CresultcardMediaScale.Hero]: ASPECT_SQUARE,
    },
    fit: CIMAGE_OBJECT_FIT.Cover,
    bleedsAtHero: false,
    adoptsIntrinsic: false,
    adoptsDecodedShape: false,
    needsIntrinsic: false,
    capsToIntrinsic: false,
    circular: true,
    opticalPadding: 0,
  },
  [CresultcardMediaKind.Native]: {
    // A stand-in, never the answer: the frame is meant to be wearing the
    // asset's own ratio by the time a reader sees it, from the caller's
    // captured integers where those exist and from the decoded bitmap where
    // they do not. It exists because the alternative is a `display: block` box
    // with no declared height whose only children are absolutely positioned —
    // which is a 0px frame, and a 0px frame is forever when no intrinsics were
    // ever declared. A square at `Thumb` because that slot is a fixed square
    // (D12b), and 4:3 above it as the least committal photographic shape.
    aspect: {
      [CresultcardMediaScale.Thumb]: ASPECT_SQUARE,
      [CresultcardMediaScale.Face]: ASPECT_FOUR_THREE,
      [CresultcardMediaScale.Hero]: ASPECT_FOUR_THREE,
    },
    fit: CIMAGE_OBJECT_FIT.Contain,
    bleedsAtHero: false,
    adoptsIntrinsic: true,
    adoptsDecodedShape: true,
    needsIntrinsic: true,
    capsToIntrinsic: true,
    circular: false,
    opticalPadding: 0,
  },
  [CresultcardMediaKind.MapTile]: {
    aspect: {
      [CresultcardMediaScale.Thumb]: ASPECT_SIXTEEN_NINE,
      [CresultcardMediaScale.Face]: ASPECT_SIXTEEN_NINE,
      [CresultcardMediaScale.Hero]: ASPECT_FOUR_THREE,
    },
    fit: CIMAGE_OBJECT_FIT.Cover,
    bleedsAtHero: false,
    adoptsIntrinsic: false,
    adoptsDecodedShape: false,
    needsIntrinsic: false,
    capsToIntrinsic: false,
    circular: false,
    opticalPadding: 0,
  },
  [CresultcardMediaKind.Frame]: {
    aspect: {
      [CresultcardMediaScale.Thumb]: ASPECT_SIXTEEN_NINE,
      [CresultcardMediaScale.Face]: ASPECT_SIXTEEN_NINE,
      [CresultcardMediaScale.Hero]: ASPECT_SIXTEEN_NINE,
    },
    fit: CIMAGE_OBJECT_FIT.Cover,
    bleedsAtHero: true,
    adoptsIntrinsic: false,
    adoptsDecodedShape: false,
    needsIntrinsic: false,
    capsToIntrinsic: false,
    circular: false,
    opticalPadding: 0,
  },
  [CresultcardMediaKind.Diagram]: {
    // Never a fixed aspect (§4.1): a floor plan's shape is the document's, so
    // the frame takes the pixels' own ratio and the decoded bitmap counts as
    // those pixels when a filing page arrives without declared dimensions.
    aspect: {
      [CresultcardMediaScale.Thumb]: null,
      [CresultcardMediaScale.Face]: null,
      [CresultcardMediaScale.Hero]: null,
    },
    fit: CIMAGE_OBJECT_FIT.Contain,
    bleedsAtHero: false,
    adoptsIntrinsic: true,
    adoptsDecodedShape: true,
    needsIntrinsic: false,
    capsToIntrinsic: true,
    circular: false,
    opticalPadding: 0,
  },
  [CresultcardMediaKind.Chart]: {
    aspect: {
      [CresultcardMediaScale.Thumb]: null,
      [CresultcardMediaScale.Face]: null,
      [CresultcardMediaScale.Hero]: null,
    },
    fit: CIMAGE_OBJECT_FIT.Contain,
    bleedsAtHero: false,
    adoptsIntrinsic: false,
    adoptsDecodedShape: false,
    needsIntrinsic: false,
    capsToIntrinsic: true,
    circular: false,
    opticalPadding: 0,
  },
};

interface CresultcardIntrinsicAspectInput {
  readonly kind: CresultcardMediaKind;
  readonly scale: CresultcardMediaScale;
  /**
   * The asset's own pixel dimensions, from whichever source the KIND allows:
   * the caller's declared capture always, and the decoded bitmap as well where
   * `adoptsDecodedShape` says the declared aspect was only ever a stand-in. The
   * frame resolves the source; this function only weighs the ratio.
   */
  readonly intrinsicWidth?: number;
  readonly intrinsicHeight?: number;
}

/**
 * The ratio the frame reserves BEFORE the asset loads, so nothing reflows and
 * a grid of covers never re-lays itself out. A kind that adopts intrinsic
 * ratios only does so when the pixels are known and plausible; a cover outside
 * the 0.5–1.0 band falls back to the kind default rather than trusting a
 * capture that says a paperback is wider than it is tall.
 */
export function resolveCresultcardAspect({
  kind,
  scale,
  intrinsicWidth,
  intrinsicHeight,
}: CresultcardIntrinsicAspectInput): CresultcardAspect {
  const policy = CRC_MEDIA_POLICY[kind];
  const declared = policy.aspect[scale];
  if (
    !policy.adoptsIntrinsic ||
    intrinsicWidth == null ||
    intrinsicHeight == null ||
    intrinsicWidth <= 0 ||
    intrinsicHeight <= 0
  ) {
    return declared;
  }
  const intrinsic = intrinsicWidth / intrinsicHeight;
  if (kind !== CresultcardMediaKind.Cover) {
    return intrinsic;
  }
  const withinBand =
    intrinsic >= CRC_COVER_INTRINSIC_MIN &&
    intrinsic <= CRC_COVER_INTRINSIC_MAX;
  return withinBand ? intrinsic : declared;
}

interface CresultcardNativeHeightInput {
  readonly containerWidth: number;
  readonly intrinsicWidth: number;
  readonly intrinsicHeight: number;
  /** Which ceiling applies: a row's lead slot and a hero are not one bound. */
  readonly scale: CresultcardMediaScale;
}

export interface CresultcardNativeHeight {
  readonly height: number;
  /** True when the ceiling bit, so the frame can say so on its face. */
  readonly contained: boolean;
}

/**
 * A `Native` frame's height comes from the measured container width and the
 * asset's own pixels — never from a constant a pillar predicted the layout
 * with, which is the mechanism that produced nine wrong widths in one file.
 */
export function resolveCresultcardNativeHeight({
  containerWidth,
  intrinsicWidth,
  intrinsicHeight,
  scale,
}: CresultcardNativeHeightInput): CresultcardNativeHeight {
  if (containerWidth <= 0 || intrinsicWidth <= 0 || intrinsicHeight <= 0) {
    return { height: 0, contained: false };
  }
  const ceiling = CRC_NATIVE_HEIGHT_CEILING[scale];
  const derived = (containerWidth * intrinsicHeight) / intrinsicWidth;
  const contained = derived > ceiling;
  return {
    height: contained ? ceiling : derived,
    contained,
  };
}
